import type { Ref } from 'vue';

/**
 * Options for the returnFocusToTrigger function
 */
export interface ReturnFocusToTriggerOptions {
  /**
   * Whether to use double requestAnimationFrame for focus restoration.
   * Use this when the modal has complex animations that may interfere with focus.
   * @default false
   */
  useDoubleRaf?: boolean;
}

/**
 * Returns focus to the trigger element when a dialog/modal is hidden.
 *
 * This function handles all the edge cases for focus restoration:
 * - Checks if the trigger element is still in the DOM
 * - Ensures the element is focusable (adds tabindex if needed)
 * - Cleans up any added tabindex after focus
 * - Uses requestAnimationFrame for proper timing after modal animations
 *
 * @param triggerElement - Ref to the element that triggered the modal
 * @param options - Optional configuration
 *
 * @example
 * ```ts
 * const triggerElement = ref<HTMLElement | null>(null);
 *
 * function onHide() {
 *   returnFocusToTrigger(triggerElement);
 * }
 * ```
 */
export function returnFocusToTrigger(
  triggerElement: Ref<HTMLElement | null>,
  options: ReturnFocusToTriggerOptions = {}
): void {
  const { useDoubleRaf = false } = options;

  if (
    triggerElement.value &&
    typeof triggerElement.value.focus === 'function' &&
    document.contains(triggerElement.value)
  ) {
    // Track if we added tabindex so we can clean it up
    const addedTabindex = !triggerElement.value.hasAttribute('tabindex');
    if (addedTabindex) {
      triggerElement.value.setAttribute('tabindex', '-1');
    }

    const focusAndCleanup = () => {
      if (triggerElement.value) {
        triggerElement.value.focus();
        // Clean up the tabindex we added
        if (addedTabindex) {
          triggerElement.value.removeAttribute('tabindex');
        }
      }
    };

    if (useDoubleRaf) {
      // Use double requestAnimationFrame for complex animations
      // This ensures focus happens AFTER BaseModal's focus restoration
      requestAnimationFrame(() => {
        requestAnimationFrame(focusAndCleanup);
      });
    } else {
      // Single requestAnimationFrame is usually sufficient
      requestAnimationFrame(focusAndCleanup);
    }
  }
}
